import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Photography } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const PhotographiesLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedPhotographies, setPaginationRelatedPhotographies] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedPhotography",
    getPaginationRelatedPhotographies.pageSize,
    getPaginationRelatedPhotographies.page *
      getPaginationRelatedPhotographies.pageSize,
  );

  const { data: countRelatedPhotographies } = useCountRelatedFields(id, type, [
    "relatedPhotography",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedPhotography",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedPhotography",
  );

  const [searchContent, setSearchContent] = useState("");

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedPhotographies } =
    useGetNotRelatedResource<Photography>({
      resultType: "photography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (photographyId: string) => {
      deleteRelation(photographyId);
    },
    [deleteRelation],
  );

  const columns: ColumnDef<Photography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "photosDescriptionNumeroEnregistrement",
        header: t("photosDescriptionNumeroEnregistrement"),
        enableResizing: true,
        size: 50,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"photography"}
            />
          );
        },
      },
      {
        accessorKey: "photosDescriptionTitre",
        header: t("photosDescriptionTitre"),
        size: 200,
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionTechnique",
        header: t("photosDescriptionTechnique"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionTheme",
        header: t("photosDescriptionTheme"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionDate",
        header: t("photosDescriptionDate"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="photography"
                  id={row.original.id}
                  elements={[
                    {
                      label: t("photosDescriptionNumeroEnregistrement"),
                      value: row.original.photosDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("photosDescriptionLegendeRedigee"),
                      value: row.original.photosDescriptionLegendeRedigee,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments,
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="photography"
                id={row.original.id}
                elements={[
                  {
                    label: t("photosDescriptionNumeroEnregistrement"),
                    value: row.original.photosDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("photosDescriptionLegendeRedigee"),
                    value: row.original.photosDescriptionLegendeRedigee,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedPhotographies")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode
            ? (notRelatedPhotographies?.data ?? [])
            : (relatedFields ?? [])
        }
        pagination={
          isAddMode
            ? {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: notRelatedPhotographies?.totalSize ?? 0,
              }
            : {
                pagination: getPaginationRelatedPhotographies,
                setPage: setPaginationRelatedPhotographies,
                totalSize: countRelatedPhotographies?.relatedPhotography,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default PhotographiesLinks;
